import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { Fleet, User, UserRole } from "../../types/auth";
import { apiService } from "../../services/api";
import { isFleetScopedRole } from "../../utils/authRoles";
import { TemporaryPasswordModal } from "./TemporaryPasswordModal";

interface UserModalProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  user?: User | null;
  onSaved: () => void;
}

export const UserModal: React.FC<UserModalProps> = ({
  open,
  onClose,
  mode,
  user,
  onSaved,
}) => {
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("fleet");
  const [fleetId, setFleetId] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const showFleetSelect = isFleetScopedRole(role);

  useEffect(() => {
    if (!open) return;
    apiService
      .getFleets()
      .then((r) => setFleets(r.fleets || []))
      .catch(console.error);
    setError("");
    setTempPassword(null);
    setCreatePassword("");
    if (mode === "create") {
      setFullName("");
      setEmail("");
      setRole("fleet");
      setFleetId("");
      setCreatePassword("");
    } else if (user) {
      setFullName(user.full_name);
      setEmail(user.email);
      setRole(user.role);
      setFleetId(user.fleet_id ?? "");
    }
  }, [open, mode, user]);

  useEffect(() => {
    if (!showFleetSelect) {
      setFleetId("");
    }
  }, [showFleetSelect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setError("Name and email are required");
      return;
    }
    if (showFleetSelect && !fleetId) {
      setError("Fleet is required for fleet-scoped users");
      return;
    }
    if (mode === "create" && role === "admin" && !createPassword.trim()) {
      setError("Password is required when creating an admin user");
      return;
    }

    setLoading(true);
    setError("");
    try {
      if (mode === "create") {
        const result = await apiService.createFleetUser({
          email: email.trim(),
          full_name: fullName.trim(),
          role,
          fleet_id: showFleetSelect ? fleetId : undefined,
          password: role === "admin" ? createPassword.trim() : undefined,
        });
        if (result.temporary_password) {
          setTempPassword(result.temporary_password);
          onSaved();
          return;
        }
      } else if (user) {
        await apiService.updateUser(user.id, {
          email: email.trim(),
          full_name: fullName.trim(),
          role,
          fleet_id: showFleetSelect ? fleetId : null,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  if (tempPassword) {
    return (
      <TemporaryPasswordModal
        password={tempPassword}
        onClose={() => {
          setTempPassword(null);
          onClose();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500/75" onClick={onClose} />
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {mode === "create" ? "Add User" : "Edit User"}
                </h3>
                <button type="button" onClick={onClose}>
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {error && (
                <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </p>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full name *
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="fleet">Fleet</option>
                    <option value="user">User</option>
                  </select>
                </div>
                {showFleetSelect && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fleet *
                    </label>
                    <select
                      value={fleetId}
                      onChange={(e) => setFleetId(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value=""></option>
                      {fleets.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {mode === "create" && role === "admin" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={createPassword}
                      onChange={(e) => setCreatePassword(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : mode === "create"
                    ? "Create User"
                    : "Save"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
